export type StepsSendDocuments = 'prescription' | 'otherDocuments' | 'anvisa' | 'laudo_medico'

export interface PrescriptionResponse {
  data: data
}

export interface data {
  recipeSalved?: RecipeSalved | null
  document?: Document | null
  success: boolean
}

export interface RecipeSalved {
  id: string
  link_pdf: string
  filename: string
  uuid: string
  extension: string
}

export interface DocumentResponse {
  data: data
}

export interface Document {
  id: string
  type: string
  url: string
  status: string
  extension: string
  originalName: string
}

export interface IPrescription {
  expireDate: string | null
  isValidPrescription: boolean
  patientName: string | null
  patientCPF: string | null
  products: IProduct[]
  doctor: IDoctor | null
  valid: {
    approved: boolean
    reason?: string
    reasons?: string[]
    validations?: {
      products_match?: {
        passed: boolean
        expected: string
        actual: string
        details?: {
          matches: any[]
        }
      }
      products_match_brand?: {
        passed: boolean
        expected: string
        actual: string
        details?: {
          matches: any[]
        }
      }
    }

  }
}

export interface IProduct {
  name: string | null
  units: number
  spectrum: string | null
  volume: number | null
  unit_of_measurement: string | null
  cannabinoids: ICannabinoid[]
}

export interface ICannabinoid {
  name: string | null
  miligram: number | null
  unit: string | null
}

export interface IDoctor {
  name: string | null
  state: string | null
  nr_council: string | null
  council: string
}

export interface IIdentityDocument {
  name: string | null
  nacionality: string | null
  cpf: string | null
  birthDate: string | null
  type: string | null
  validity: string | null
  rg: string | null
  isValid: boolean
  expeditionDate: string | null
  pathernship: string | null
  maternity: string | null
  valid: {
    approved: boolean
    reason?: string
    reasons?: string[]
    requiresIdentityResponsibleDocument?: boolean
    identityResponsibleData?: {
      identityName?: string | null
      similarity?: number
      reason?: string
    }
  }
}

export interface IAddressDocument {
  name: string | null
  address: string | null
  city: string | null
  state: string | null
  cep: string | null
  isValid: boolean
  expirationDate: string | null
  expireDate: string | null
  street: string | null
  number: string | null
  neighborhood: string | null
  complement: string | null
  valid: {
    approved: boolean
    reason?: string
    reasons?: string[]
  }
}

export interface IAnvisaDocument {
  name: string | null
  cpf: string | null
  expirationDate: string | null
  doctor: IAnvisaDoctor | null
  products: IAnvisaProduct[]
  valid: {
    approved: boolean
    reason?: string
    reasons?: string[]
  }
}

export interface IAnvisaDoctor {
  name: string | null
  crm: string | null
  state: string | null
}

export interface IAnvisaProduct {
  name: string | null
}

export interface IAffiliationDocument {
  names: IName[]
  cpf: string | null
  birthDate: string | null
  type: string
  rg: string | null
  isValid: boolean
  valid: {
    approved: boolean
    reason?: string
    reasons?: string[]
  }
}

export interface IName {
  name: string | null
}

export interface IResponsibleIdentityDocument {
  name: string | null
  cpf: string | null
  birthDate: string | null
  type: string
  rg: string | null
  isValid: boolean
  pathernship: string | null
  maternity: string | null
  valid: {
    approved: boolean
    reason?: string
    reasons?: string[]
  }
}

export type DocumentType =
  | 'prescription'
  | 'identityDocument'
  | 'address'
  | 'anvisa'
  | 'laudo_medico'
  | 'affiliation'
  | 'responsibleIdentityDocument'

export type DocumentData =
  | IPrescription
  | IIdentityDocument
  | IAddressDocument
  | IAnvisaDocument
  | IAffiliationDocument
  | IResponsibleIdentityDocument

export interface IDocumentResponse<T = DocumentData> {
  type: DocumentType
  data: T
  success: boolean
  error?: string
  timestamp?: string
}

export interface IDocumentProcessingRequest {
  type: DocumentType
  data: string
  options?: {
    validateOnly?: boolean
    extractAll?: boolean
  }
}

export type StepResponseMap = {
  prescription: {
    normal: PrescriptionResponse
    ocr: IPrescription
  }
  pill_prescription: {
    normal: PrescriptionResponse
    ocr: IPrescription
  }
  anvisa: {
    normal: DocumentResponse
    ocr: IAnvisaDocument
  }
  laudo_medico: {
    normal: DocumentResponse
    ocr: IAnvisaDocument
  }
  identity: {
    normal: DocumentResponse
    ocr: IIdentityDocument
  }
  address: {
    normal: DocumentResponse
    ocr: IAddressDocument
  }
  powerOfAttorney: {
    normal: DocumentResponse
    ocr: IAffiliationDocument
  }
  affiliation: {
    normal: DocumentResponse
    ocr: IAffiliationDocument
  }
  responsibleIdentity: {
    normal: DocumentResponse
    ocr: IResponsibleIdentityDocument
  }
}

interface DocumentStep<T extends keyof StepResponseMap> {
  endpointWithOCR: string
  endpoint: string
  description: string
  responses: {
    normal: StepResponseMap[T]['normal'] | null
    ocr: StepResponseMap[T]['ocr'] | null
  }
}

export const StepsSendDocuments = (order_id: string, origin_checkout?: string) => ({
  prescription: {
    endpointWithOCR: `/ocr/validate-file?type=RECEITA&origin_checkout=${origin_checkout}`,
    endpoint: `/order/send_recipe/${order_id}`,
    description: 'prescription',
    responses: {
      normal: {} as PrescriptionResponse,
      ocr: {} as IPrescription
    }
  } as DocumentStep<'prescription'>,
  pill_prescription: {
    endpointWithOCR: '',
    endpoint: `/order/send_recipe/${order_id}`,
    description: 'pill_prescription',
    responses: {
      normal: {} as PrescriptionResponse,
      ocr: {} as IPrescription
    }
  } as DocumentStep<'pill_prescription'>,
  anvisa: {
    endpointWithOCR: `/ocr/validate-file?type=ANVISA&origin_checkout=${origin_checkout}`,
    endpoint: `/order/document/${order_id}?type=ANVISA&emission=18%2F11%2F2022`,
    description: 'anvisa',
    responses: {
      normal: {} as DocumentResponse,
      ocr: {} as IAnvisaDocument
    }
  } as DocumentStep<'anvisa'>,
  laudo_medico: {
    endpointWithOCR: `/ocr/validate-file?type=LAUDO_MEDICO&origin_checkout=${origin_checkout}`,
    endpoint: `/order/document/${order_id}?type=LAUDO_MEDICO&emission=18%2F11%2F2022`,
    description: 'laudo_medico',
    responses: {
      normal: {} as DocumentResponse,
      ocr: {} as IAnvisaDocument
    }
  } as DocumentStep<'laudo_medico'>,
  identity: {
    endpointWithOCR: `/ocr/validate-file?type=IDENTIDADE&origin_checkout=${origin_checkout}`,
    endpoint: `/order/document/${order_id}?type=IDENTIDADE&emission=18%2F11%2F2022`,
    description: 'identityDocument',
    responses: {
      normal: {} as DocumentResponse,
      ocr: {} as IIdentityDocument
    }
  } as DocumentStep<'identity'>,
  address: {
    endpointWithOCR: `/ocr/validate-file?type=RESIDENCIAL&origin_checkout=${origin_checkout}`,
    endpoint: `/order/document/${order_id}?type=RESIDENCIAL&emission=18%2F11%2F2022`,
    description: 'address',
    responses: {
      normal: {} as DocumentResponse,
      ocr: {} as IAddressDocument
    }
  } as DocumentStep<'address'>,
  affiliation: {
    endpointWithOCR: `/ocr/validate-file?type=VINCULO&origin_checkout=${origin_checkout}`,
    endpoint: `/order/document/${order_id}?type=VINCULO&emission=18%2F11%2F2022`,
    description: 'affiliation',
    responses: {
      normal: {} as DocumentResponse,
      ocr: {} as IAffiliationDocument
    }
  } as DocumentStep<'affiliation'>,
  responsibleIdentity: {
    endpointWithOCR: `/ocr/validate-file?type=IDENTIDADE_RESPONSAVEL&origin_checkout=${origin_checkout}`,
    endpoint: `/order/document/${order_id}?type=IDENTIDADE_RESPONSAVEL&emission=18%2F11%2F2022`,
    description: 'responsibleIdentity',
    responses: {
      normal: {} as DocumentResponse,
      ocr: {} as IResponsibleIdentityDocument
    }
  } as DocumentStep<'responsibleIdentity'>
})

export interface DocumentSuccessCallbacks {
  onSuccess?: () => void
}

export class StepSendDocumentsManager {
  private steps: ReturnType<typeof StepsSendDocuments>

  constructor(order_id: string, origin_checkout?: string) {
    this.steps = StepsSendDocuments(order_id, origin_checkout)
  }

  setNormalResponse<T extends keyof StepResponseMap>(stepName: T, response: StepResponseMap[T]['normal']): void {
    this.steps[stepName as keyof typeof this.steps].responses.normal = response
  }

  setOCRResponse<T extends keyof StepResponseMap>(stepName: T, response: StepResponseMap[T]['ocr']): void {
    this.steps[stepName as keyof typeof this.steps].responses.ocr = response
  }

  getNormalResponse<T extends keyof StepResponseMap>(stepName: T): StepResponseMap[T]['normal'] | null {
    return this.steps[stepName as keyof typeof this.steps].responses.normal
  }

  getOCRResponse<T extends keyof StepResponseMap>(stepName: T): StepResponseMap[T]['ocr'] | null {
    return this.steps[stepName as keyof typeof this.steps].responses.ocr
  }

  getEndpoint(stepName: keyof StepResponseMap, responseType: 'normal' | 'ocr'): string {
    const step = this.steps[stepName as keyof typeof this.steps]
    return responseType === 'normal' ? step.endpoint : step.endpointWithOCR
  }

  getDescription(stepName: keyof StepResponseMap): string {
    return this.steps[stepName as keyof typeof this.steps].description
  }

  hasResponse(stepName: keyof StepResponseMap, responseType: 'normal' | 'ocr'): boolean {
    return this.steps[stepName as keyof typeof this.steps].responses[responseType] !== null
  }

  isStepComplete(stepName: keyof StepResponseMap): boolean {
    const step = this.steps[stepName as keyof typeof this.steps]
    return step.responses.normal !== null && step.responses.ocr !== null
  }
}
